import { useEffect } from 'react';
import { Checkbox, Text } from '@belong/ui';
import type { PaymentAccountResourceModel } from 'api/models';
import { IconDots } from '@belong/icons';
import { PAYMENT_SCREENS } from 'containercomponents/Modals/PaymentsModal/PaymentsModal';
import PaymentMenuSelectionContainer from 'containers/PaymentSelectionMenuContainer/PaymentMenuSelectionContainer';
import { PaymentPreference } from 'models/enums';
import { FEEDBACK_STRINGS } from 'strings/errors.string';
import { FAILED_PAYMENTS_STRINGS } from '../failed-payments-strings';
import { mapPaymentAccountToPaymentMethodSummary } from '../failed-payments-utils';
import { PaymentMethodSummary } from '../payment-method-summary';

type ChangePaymentMethodSubsectionProps = {
  paymentMethods: PaymentAccountResourceModel[];
  selectedPaymentAccountUniqueId: string | null;
  setAsDefault: boolean;
  onSelectPaymentMethod: (id: string) => void;
  onSetAsDefaultChange: (value: boolean) => void;
};

export function ChangePaymentMethodSubsection({
  paymentMethods,
  selectedPaymentAccountUniqueId,
  setAsDefault,
  onSelectPaymentMethod,
  onSetAsDefaultChange,
}: ChangePaymentMethodSubsectionProps) {
  const availablePaymentMethods = paymentMethods.filter(
    (paymentMethod) => paymentMethod.accountId && paymentMethod.isVerified,
  );
  const selectedPaymentMethod =
    availablePaymentMethods.find(
      (paymentMethod) =>
        paymentMethod.accountId === selectedPaymentAccountUniqueId,
    ) ?? availablePaymentMethods[0];
  const selectedPaymentMethodSummary = selectedPaymentMethod
    ? mapPaymentAccountToPaymentMethodSummary(selectedPaymentMethod)
    : null;

  useEffect(() => {
    if (!selectedPaymentAccountUniqueId && selectedPaymentMethod?.accountId) {
      onSelectPaymentMethod(selectedPaymentMethod.accountId);
    }
  }, [
    onSelectPaymentMethod,
    selectedPaymentAccountUniqueId,
    selectedPaymentMethod?.accountId,
  ]);

  const paymentModalProps = {
    screen: PAYMENT_SCREENS.PAYMENT_SELECTION,
    errorScreenProps: {
      title: FEEDBACK_STRINGS.set_payment_method.error.title,
      ctaLabel: FEEDBACK_STRINGS.set_payment_method.error.ctaLabel,
    },
    successScreenProps: {
      title: FEEDBACK_STRINGS.set_payment_method.success.title,
      ctaLabel: FEEDBACK_STRINGS.set_payment_method.success.ctaLabel,
    },
  };

  return (
    <div className="flex flex-col items-end gap-md">
      <Text variant="h3" fontWeight="semibold" className="w-full">
        {FAILED_PAYMENTS_STRINGS['change_payment_method.prompt']}
      </Text>

      {selectedPaymentMethodSummary && (
        <div className="w-full">
          <PaymentMenuSelectionContainer
            selectedPaymentMethodId={selectedPaymentAccountUniqueId ?? ''}
            onSelectPaymentMethod={onSelectPaymentMethod}
            paymentType={PaymentPreference.Rent}
            paymentModalProps={paymentModalProps}
            showOnlyValidPaymentMethods
            shouldShowAutoSplitPaymentMethod={false}
            onSelectAutoSplitPaymentMethod={() => {}}
            fitToButton
            onTop
            menuButtonLabel={
              <span className="flex h-[106px] w-full items-center rounded border border-solid border-navy bg-white px-[17px] py-[34px] text-left">
                <span className="min-w-0 flex-1">
                  <PaymentMethodSummary
                    paymentMethod={selectedPaymentMethodSummary}
                  />
                </span>
                <span className="inline-flex px-xs text-navy">
                  <IconDots width={5} height={24} />
                </span>
              </span>
            }
          />
        </div>
      )}

      <Checkbox
        id="failed-payments-set-default-payment-method"
        checked={setAsDefault}
        label={FAILED_PAYMENTS_STRINGS['change_payment_method.set_as_default']}
        onChange={(checked) => onSetAsDefaultChange(checked === true)}
        variant="primary"
        labelTextClassName="uppercase text-dark-gray"
      />
    </div>
  );
}
